/**
 * Multi-source seed data generator with pre-validation.
 * Sources: picsum.photos (deterministic IDs) + Lorem Flickr (keyword-based)
 * All URLs are validated via Image() preload before DB insertion.
 */

const SEED_CATEGORIES = [
  {
    folder: "cyber-security",
    keywords: ["cybersecurity", "server", "encryption", "hacking", "network"],
    flickrKeywords: ["server,room", "cybersecurity", "data,center", "hacking,computer", "network,security"],
    tags: [
      ["cybersecurity", "hacking", "data-breach", "network", "encryption"],
      ["server-room", "data-center", "infrastructure", "cloud", "security"],
      ["encryption", "privacy", "digital-security", "vpn", "firewall"],
      ["malware", "ransomware", "threat", "cybercrime", "protection"],
      ["dark-web", "anonymity", "surveillance", "forensics", "incident-response"],
    ],
    altTemplates: [
      "Secure data encryption visualization",
      "Modern server room with blue lighting",
      "Cybersecurity analyst monitoring threats",
      "Network firewall protection dashboard",
      "Digital lock and shield concept",
    ],
  },
  {
    folder: "programming",
    keywords: ["code", "programming", "software", "developer", "terminal"],
    flickrKeywords: ["programming,code", "software,developer", "computer,terminal", "javascript,coding", "laptop,code"],
    tags: [
      ["coding", "javascript", "programming", "software", "development"],
      ["terminal", "command-line", "linux", "devops", "automation"],
      ["algorithm", "data-structure", "computer-science", "logic", "optimization"],
      ["api", "backend", "microservices", "architecture", "integration"],
      ["git", "version-control", "collaboration", "open-source", "repository"],
    ],
    altTemplates: [
      "Clean code on modern IDE screen",
      "Software engineer working on terminal",
      "JavaScript framework development",
      "API architecture visualization",
      "DevOps pipeline automation dashboard",
    ],
  },
  {
    folder: "economy-trade",
    keywords: ["finance", "stock-market", "trading", "business", "economy"],
    flickrKeywords: ["stock,market", "finance,trading", "wall,street", "business,chart", "economy,money"],
    tags: [
      ["stock-market", "trading", "finance", "wall-street", "investment"],
      ["gold", "commodities", "precious-metals", "reserves", "banking"],
      ["financial-district", "skyscraper", "business-center", "economy", "commerce"],
      ["cryptocurrency", "bitcoin", "blockchain", "defi", "digital-currency"],
      ["forex", "exchange-rate", "global-trade", "economics", "monetary-policy"],
    ],
    altTemplates: [
      "Stock exchange trading floor activity",
      "Financial district skyline at dawn",
      "Cryptocurrency market analysis dashboard",
      "Wall Street business district view",
      "Global forex trading overview",
    ],
  },
  {
    folder: "tech-ai",
    keywords: ["technology", "artificial-intelligence", "robot", "innovation", "circuit"],
    flickrKeywords: ["artificial,intelligence", "robot,technology", "innovation,tech", "circuit,board", "silicon,valley"],
    tags: [
      ["silicon-valley", "tech-industry", "innovation", "startup", "venture-capital"],
      ["smartphone", "manufacturing", "electronics", "assembly", "production"],
      ["clean-tech", "renewable-energy", "sustainability", "green-tech", "solar"],
      ["artificial-intelligence", "machine-learning", "neural-network", "deep-learning", "ai"],
      ["robotics", "automation", "industry-4.0", "manufacturing", "precision"],
    ],
    altTemplates: [
      "Silicon Valley tech campus aerial view",
      "AI neural network visualization",
      "Robotic arm in manufacturing facility",
      "Quantum computing processor closeup",
      "Tech startup modern workspace",
    ],
  },
  {
    folder: "corporate-brands",
    keywords: ["office", "business", "corporate", "meeting", "workspace"],
    flickrKeywords: ["modern,office", "business,meeting", "corporate,boardroom", "workspace,desk", "conference,room"],
    tags: [
      ["corporate", "office", "business", "workspace", "professional"],
      ["presentation", "meeting", "strategy", "leadership", "management"],
      ["boardroom", "executive", "c-suite", "decision-making", "governance"],
      ["teamwork", "collaboration", "brainstorming", "innovation", "culture"],
      ["conference", "summit", "networking", "industry-event", "keynote"],
    ],
    altTemplates: [
      "Modern corporate office open space",
      "Business strategy presentation",
      "Executive boardroom meeting",
      "Team collaboration brainstorming session",
      "Corporate conference keynote stage",
    ],
  },
  {
    folder: "politics-world",
    keywords: ["government", "politics", "parliament", "diplomacy", "flag"],
    flickrKeywords: ["government,building", "politics,parliament", "diplomacy,summit", "flag,national", "democracy,vote"],
    tags: [
      ["politics", "government", "legislation", "policy", "democracy"],
      ["summit", "diplomacy", "international-relations", "treaty", "alliance"],
      ["press-conference", "media", "journalism", "spokesperson", "briefing"],
      ["parliament", "congress", "senate", "debate", "voting"],
      ["election", "campaign", "ballot", "democracy", "civic-duty"],
    ],
    altTemplates: [
      "International diplomatic summit gathering",
      "Government press conference podium",
      "Parliament building grand facade",
      "United Nations assembly hall view",
      "Election day democratic process",
    ],
  },
  {
    folder: "science",
    keywords: ["science", "laboratory", "research", "microscope", "space"],
    flickrKeywords: ["science,laboratory", "research,microscope", "space,rocket", "dna,genetics", "physics,experiment"],
    tags: [
      ["biotechnology", "genetics", "dna", "genomics", "bioinformatics"],
      ["microscope", "laboratory", "research", "experiment", "analysis"],
      ["aerospace", "space-exploration", "rocket", "satellite", "nasa"],
      ["physics", "quantum", "particle", "accelerator", "energy"],
      ["medical", "healthcare", "pharmaceutical", "clinical-trial", "innovation"],
    ],
    altTemplates: [
      "Biotechnology DNA sequencing lab",
      "Microscope analysis in research facility",
      "Aerospace rocket launch preparation",
      "Quantum physics experiment setup",
      "Medical research breakthrough moment",
    ],
  },
  {
    folder: "sports",
    keywords: ["sports", "stadium", "athlete", "football", "competition"],
    flickrKeywords: ["sports,stadium", "athlete,running", "football,soccer", "basketball,court", "competition,race"],
    tags: [
      ["stadium", "arena", "sports-venue", "crowd", "atmosphere"],
      ["athletics", "track-field", "competition", "olympics", "champion"],
      ["football", "soccer", "match", "goal", "tournament"],
      ["basketball", "nba", "court", "dunk", "playoffs"],
      ["esports", "gaming", "tournament", "streaming", "competitive"],
    ],
    altTemplates: [
      "Packed stadium during championship match",
      "Athletic track and field competition",
      "Football goal celebration moment",
      "Basketball slam dunk action shot",
      "Tennis grand slam court overview",
    ],
  },
  {
    folder: "entrepreneurship",
    keywords: ["startup", "entrepreneur", "pitch", "coworking", "innovation"],
    flickrKeywords: ["startup,office", "entrepreneur,pitch", "coworking,space", "innovation,hub", "business,plan"],
    tags: [
      ["startup", "entrepreneurship", "founder", "innovation", "disruption"],
      ["venture-capital", "funding", "investment", "seed-round", "valuation"],
      ["incubator", "accelerator", "mentorship", "ecosystem", "community"],
      ["growth", "scaling", "product-market-fit", "traction", "metrics"],
      ["pitch", "demo-day", "presentation", "investor", "business-plan"],
    ],
    altTemplates: [
      "Startup founder pitch presentation",
      "Venture capital meeting negotiation",
      "Tech incubator collaborative workspace",
      "Growth metrics dashboard analysis",
      "Startup demo day audience",
    ],
  },
  {
    folder: "lifestyle-society",
    keywords: ["city", "urban", "people", "culture", "architecture"],
    flickrKeywords: ["city,urban", "people,culture", "architecture,building", "travel,landmark", "community,society"],
    tags: [
      ["urban", "city-life", "metropolitan", "culture", "society"],
      ["education", "university", "learning", "knowledge", "academic"],
      ["health", "wellness", "fitness", "nutrition", "mental-health"],
      ["environment", "climate", "sustainability", "nature", "conservation"],
      ["travel", "tourism", "destination", "exploration", "adventure"],
    ],
    altTemplates: [
      "Urban cityscape modern architecture",
      "University campus academic life",
      "Wellness and fitness lifestyle",
      "Environmental conservation initiative",
      "Cultural festival celebration",
    ],
  },
];

// Known missing picsum IDs to skip
const MISSING_PICSUM_IDS = new Set([
  86, 97, 105, 138, 148, 150, 205, 207, 224, 226, 245, 246, 262, 285, 286,
  298, 303, 332, 333, 346, 347, 352, 353, 354, 367, 378, 384, 394, 414, 422,
  438, 462, 463, 470, 489, 540, 561, 578, 589, 592, 714, 720, 734,
]);

/**
 * Validates an image URL by attempting to load it via an Image() object.
 * Returns true if it loads successfully within timeout.
 */
export function validateImageUrl(url: string, timeout = 8000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.src = "";
      resolve(false);
    }, timeout);
    img.onload = () => {
      clearTimeout(timer);
      // Reject tiny placeholder images
      if (img.naturalWidth < 200 || img.naturalHeight < 150) {
        resolve(false);
      } else {
        resolve(true);
      }
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });
}

/**
 * Generates a single image candidate from one of two sources.
 * Source 0 = picsum.photos (deterministic, highly reliable)
 * Source 1 = loremflickr.com (keyword-based, good variety)
 */
function generateCandidate(
  catIdx: number,
  itemIdx: number,
  picsumId: number,
  userId: string,
): {
  row: {
    user_id: string;
    file_name: string;
    file_path: string;
    file_url: string;
    file_size: number;
    mime_type: string;
    width: number;
    height: number;
    alt_text: string;
    tags: string[];
    folder: string;
  };
  source: string;
} {
  const cat = SEED_CATEGORIES[catIdx];
  const seqNum = String(itemIdx + 1).padStart(3, "0");
  const keyword = cat.keywords[itemIdx % cat.keywords.length];
  const altIdx = itemIdx % cat.altTemplates.length;
  const tagSet = cat.tags[itemIdx % cat.tags.length];

  // Alternate sources: even = picsum, odd = loremflickr
  const sourceType = itemIdx % 2;
  let url: string;
  let source: string;

  if (sourceType === 0) {
    url = `https://picsum.photos/id/${picsumId}/1200/800`;
    source = "picsum";
  } else {
    const flickrKw = cat.flickrKeywords[itemIdx % cat.flickrKeywords.length];
    // Use a unique lock param to prevent caching
    url = `https://loremflickr.com/1200/800/${flickrKw}?lock=${catIdx * 1000 + itemIdx}`;
    source = "loremflickr";
  }

  return {
    row: {
      user_id: userId,
      file_name: `${cat.folder}-${keyword}-${seqNum}.jpg`,
      file_path: `seed/${cat.folder}/${keyword}-${seqNum}.jpg`,
      file_url: url,
      file_size: 180000 + Math.floor(Math.random() * 320000),
      mime_type: "image/jpeg",
      width: 1200,
      height: 800,
      alt_text: `${cat.altTemplates[altIdx]} — ${keyword.replace(/-/g, " ")}`,
      tags: tagSet,
      folder: cat.folder,
    },
    source,
  };
}

/**
 * Gets the next valid picsum ID, skipping known missing ones.
 */
function nextPicsumId(current: number): number {
  let id = current + 1;
  while (MISSING_PICSUM_IDS.has(id) || id > 1084) {
    if (id > 1084) id = 10;
    else id++;
  }
  return id;
}

export { SEED_CATEGORIES, nextPicsumId, generateCandidate };
